import { useState } from "react";
import type { ButtonHTMLAttributes, ComponentPropsWithRef, CSSProperties, ReactNode, SelectHTMLAttributes } from "react";
import s from "./components.module.css";
import {
  PRIORITY_TITLE,
  priorityLabel,
  STATUS_LABEL,
  type WorkOrderPriority,
  type WorkOrderStatus,
} from "../../domain/workOrders";
import { severityLabel } from "../../domain/alerts";
import type { KpiGrade, ReadinessState } from "../../domain/kpis";
import { t, tc } from "../../i18n";

/** Plain text given to a component is translated where it is shown; anything richer is the caller's. */
function tr(node: ReactNode): ReactNode {
  return typeof node === "string" ? t(node) : node;
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/* ---- Button --------------------------------------------------------------- */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "primary" | "danger" | "ghost";
  size?: "md" | "sm";
}
export function Button({ variant = "default", size = "md", className, type = "button", ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        s.btn,
        variant === "primary" && s.primary,
        variant === "danger" && s.danger,
        variant === "ghost" && s.ghost,
        size === "sm" && s.sm,
        className,
      )}
      {...rest}
    />
  );
}

/* ---- Card ----------------------------------------------------------------- */

/**
 * Whether a window is rolled up, remembered per window in this browser.
 *
 * A desk that carries a catalogue, a replenishment run and a purchasing chain
 * is a long page, and somebody working one of them should be able to put the
 * others away. The choice belongs to the person and the machine, not to the
 * register, so it lives in localStorage and never reaches the platform.
 *
 * Read through try/catch: a private window, or one with site data blocked,
 * throws on access, and a window that will not draw because it could not find
 * out whether it was rolled up is worse than one that is always open.
 */
const FOLD_KEY = "sky.card.";

function readFolded(key: string): boolean {
  try {
    return localStorage.getItem(FOLD_KEY + key) === "1";
  } catch {
    return false;
  }
}

function writeFolded(key: string, folded: boolean) {
  try {
    if (folded) localStorage.setItem(FOLD_KEY + key, "1");
    else localStorage.removeItem(FOLD_KEY + key);
  } catch {
    /* storage unavailable: the choice lasts as long as the page does */
  }
}

export function Card({
  title,
  sub,
  actions,
  children,
  className,
  style,
  collapsible,
  collapseKey,
  testId,
}: {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** offer a control that rolls the window up to its title */
  collapsible?: boolean;
  /** what the choice is remembered under; the title, when it is a plain string */
  collapseKey?: string;
  testId?: string;
}) {
  const key = collapseKey ?? (typeof title === "string" ? title : "");
  const [folded, setFolded] = useState(() => (collapsible && key ? readFolded(key) : false));
  const canFold = Boolean(collapsible) && key !== "";
  const bodyId = `card-${key.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`;

  const fold = () => {
    const next = !folded;
    setFolded(next);
    writeFolded(key, next);
  };

  return (
    <section className={cx(s.card, folded && s.cardFolded, className)} style={style} data-testid={testId}>
      {(title || actions || canFold) && (
        <header className={s.cardHead}>
          <div>
            {title && <h2 className={s.cardTitle}>{tr(title)}</h2>}
            {/* the subtitle explains the contents, so it goes away with them */}
            {sub && !folded && <div className={s.cardSub}>{tr(sub)}</div>}
          </div>
          <div className={s.cardTools}>
            {/* the window's own actions are meaningless while it is rolled up */}
            {!folded && actions}
            {canFold && (
              <button
                type="button"
                className={s.fold}
                onClick={fold}
                aria-expanded={!folded}
                aria-controls={bodyId}
                aria-label={folded ? t("Open {name}", { name: key }) : t("Minimise {name}", { name: key })}
                /* "Open" is already an adjective in this app — an open work
                   order — and the button needs the verb, so it carries its
                   context */
                title={folded ? tc("Open", "card") : t("Minimise")}
                data-testid={`fold-${bodyId}`}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  {folded ? (
                    <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  ) : (
                    <path d="M5 12h14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  )}
                </svg>
              </button>
            )}
          </div>
        </header>
      )}
      <div id={bodyId} hidden={folded}>
        {children}
      </div>
    </section>
  );
}

/* ---- Badges --------------------------------------------------------------- */
const STATUS_COLOUR: Record<WorkOrderStatus, string> = {
  draft: "var(--dim)",
  approved: "var(--blue)",
  dispatched: "var(--blue)",
  in_progress: "var(--amber)",
  on_hold: "var(--p2)",
  completed: "var(--green)",
  verified: "var(--go)",
  cancelled: "var(--dim)",
};

export function StatusBadge({ status }: { status: WorkOrderStatus }) {
  return (
    <span className={s.badge} style={{ "--badge-c": STATUS_COLOUR[status] } as CSSProperties}>
      {t(STATUS_LABEL[status])}
    </span>
  );
}

export function PriorityBadge({ priority }: { priority: WorkOrderPriority }) {
  const c = `var(--p${priority})`;
  return (
    <span
      className={cx(s.badge, s.badgeSolid)}
      style={{ "--badge-c": c } as CSSProperties}
      title={t(PRIORITY_TITLE[priority])}
    >
      {t(priorityLabel(priority))}
    </span>
  );
}

/** Alert severity 1–5: reuses the priority colour language in reverse (5 = critical = P1 red). */
export function SeverityBadge({ severity }: { severity: number }) {
  const p = Math.min(5, Math.max(1, 6 - severity));
  return (
    <span
      className={cx(s.badge, s.badgeSolid)}
      style={{ "--badge-c": `var(--p${p})` } as CSSProperties}
      title={t(severityLabel(severity))}
    >
      S{severity}
    </span>
  );
}

const STATE_COLOUR: Record<ReadinessState, string> = {
  go: "var(--go)",
  caution: "var(--caution)",
  nogo: "var(--nogo)",
  idle: "var(--idle)",
};
const STATE_LABEL: Record<ReadinessState, string> = { go: "GO", caution: "CAUTION", nogo: "NO-GO", idle: "NO DATA" };

export function StateBadge({ state }: { state: ReadinessState }) {
  return (
    <span className={s.badge} style={{ "--badge-c": STATE_COLOUR[state] } as CSSProperties}>
      {t(STATE_LABEL[state])}
    </span>
  );
}

export function gradeColour(grade: KpiGrade | null): string {
  return grade ? `var(--grade-${grade.toLowerCase()})` : "var(--idle)";
}

/* ---- Table ---------------------------------------------------------------- */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>{children}</table>
    </div>
  );
}
export const tableRowLinkClass = s.rowLink;
export const numClass = s.num;

/* ---- States --------------------------------------------------------------- */
export function EmptyState({ children }: { children: ReactNode }) {
  return <div className={s.state}>{tr(children)}</div>;
}
export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <div className={cx(s.state, s.stateError)} role="alert">
      {msg}
      {retry && (
        <div style={{ marginTop: 10 }}>
          <Button size="sm" onClick={retry}>
            {t("Try again")}
          </Button>
        </div>
      )}
    </div>
  );
}
export function Skeleton({ rows = 4, height = 14 }: { rows?: number; height?: number }) {
  return (
    <div style={{ display: "grid", gap: 8 }} aria-busy="true" aria-label={t("Loading")}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={s.skeleton} style={{ height, width: `${70 + ((i * 13) % 30)}%` }} />
      ))}
    </div>
  );
}

/* ---- Segmented ------------------------------------------------------------ */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div className={s.seg} role="group" aria-label={t(ariaLabel)}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {t(o.label)}
        </button>
      ))}
    </div>
  );
}

/* ---- Inputs --------------------------------------------------------------- */
/*
 * `ComponentPropsWithRef`, not `InputHTMLAttributes`: React 19 passes a ref as
 * an ordinary prop, and the narrower type refused it — so a screen that needed
 * to hold the focus, like the stores counter's scan box, could not.
 */
export function Input(props: ComponentPropsWithRef<"input">) {
  return <input {...props} className={cx(s.input, props.className)} />;
}
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(s.input, props.className)} />;
}
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className={s.label}>
      <span>{t(label)}</span>
      {children}
    </label>
  );
}
